/* BOHDY landing — live menu boards on the 5-panel menu wall.
 *
 * The cafe's menu wall is a row of five portrait TV panels (plus the four
 * order tablets below, which stay as photographed). This module pins a live
 * <iframe> of the real menu-board app (?screen=1…5) onto each panel with a
 * CSS matrix3d homography, in two photos:
 *
 *   - the interior hero photo sees the wall edge-on at the right of frame:
 *     panel 1 is fully visible, panel 2 partially, 3-5 are past the frame
 *     edge (their quads project offstage and are skipped);
 *   - the "Kiosk + menu" fly-in photo (scene-menu.jpg) faces the wall, so all
 *     five boards appear when the camera flies into the Food-menu hotspot.
 *
 * Projection modes, matching how the hero renders:
 *   - static: plain object-fit cover/contain mapping over the <img> fallback
 *     (hero quads only — without 3D the destination photo never shows).
 *   - 3D: bohdy-hero3d.js calls root._bohdyScreens(state) every frame with
 *     its camera uniforms for BOTH scenes (zoom / focus / pan / drift / depth
 *     strength / crossfade mix / destination url), and the quads are
 *     re-projected through the same math the shader uses. While the fly-in
 *     crossfade runs, the hero boards fade out, then — if the destination is
 *     the bundled menu-wall photo — all five boards fade back in glued to
 *     the wall in that photo. Flying back out reverses it.
 *
 * The quads are authored against the bundled photos, so the module bails
 * unless the hero source still points at the bundled interior (HERO_KEY),
 * and the destination quads only engage for scene-menu (MENU_KEY).
 */

(function () {
  'use strict';

  var SECTION_SELECTOR = '.bohdy';
  var HERO_KEY = 'Bohdys_Interior_and_Models';
  var MENU_KEY = 'scene-menu';
  var FALLBACK_ASPECT = 6048 / 4032;

  // Photo-space corners (TL, TR, BR, BL) as [u, v, depth]:
  //   u/v    fraction of the photo from the top-left,
  //   depth  sampled from the photo's depth map (near = 1) — drives how much
  //          the drift/pointer parallax shifts each corner, like the shader.
  // Fitted as one flat wall plane (left edge + panel seams -> homography),
  // then split into 5 equal panels, so the boards land on the physical seams.

  // The wall as the hero photo sees it (steep angle, right of frame).
  // Panels 3-5 lie past u = 1 (outside the photo) and stay offstage.
  var HERO_SCREENS = [
    [[0.8752, 0.1551, 0.255], [0.9709, 0.1003, 0.305], [0.9709, 0.3988, 0.322], [0.8749, 0.4131, 0.265]],
    [[0.9755, 0.0977, 0.307], [1.1087, 0.0215, 0.327], [1.1093, 0.3782, 0.333], [0.9755, 0.3981, 0.324]],
    [[1.1153, 0.0177, 0.331], [1.3134, -0.0956, 0.314], [1.3153, 0.3475, 0.329], [1.1160, 0.3772, 0.333]],
    [[1.3236, -0.1015, 0.314], [1.6493, -0.2879, 0.314], [1.6544, 0.2970, 0.329], [1.3257, 0.3460, 0.329]],
    [[1.6673, -0.2981, 0.314], [2.3022, -0.6615, 0.314], [2.3170, 0.1982, 0.322], [1.6726, 0.2943, 0.329]]
  ];

  // The same wall in the Food-menu fly-in photo (scene-menu.jpg), face-on.
  var MENU_SCREENS = [
    [[0.3002, 0.2172, 0.091], [0.3777, 0.2173, 0.097], [0.3786, 0.4278, 0.105], [0.3013, 0.4282, 0.100]],
    [[0.3809, 0.2173, 0.098], [0.4580, 0.2173, 0.106], [0.4587, 0.4274, 0.109], [0.3818, 0.4278, 0.105]],
    [[0.4612, 0.2173, 0.106], [0.5380, 0.2174, 0.099], [0.5385, 0.4270, 0.109], [0.4619, 0.4274, 0.108]],
    [[0.5412, 0.2174, 0.099], [0.6177, 0.2174, 0.100], [0.6179, 0.4266, 0.109], [0.5417, 0.4270, 0.109]],
    [[0.6209, 0.2175, 0.100], [0.6970, 0.2175, 0.108], [0.6970, 0.4262, 0.105], [0.6211, 0.4265, 0.109]]
  ];

  var instances = new WeakMap();

  // CSS matrix3d mapping the wrapper's (0,0)-(w,h) box onto an arbitrary
  // screen-space quad (TL, TR, BR, BL) — classic unit-square homography.
  function quadMatrix(w, h, p) {
    var x0 = p[0][0], y0 = p[0][1], x1 = p[1][0], y1 = p[1][1];
    var x2 = p[2][0], y2 = p[2][1], x3 = p[3][0], y3 = p[3][1];
    var dx1 = x1 - x2, dy1 = y1 - y2, dx2 = x3 - x2, dy2 = y3 - y2;
    var sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
    var den = dx1 * dy2 - dx2 * dy1;
    if (!den) return null;
    var g = (sx * dy2 - dx2 * sy) / den;
    var hh = (dx1 * sy - sx * dy1) / den;
    var a = x1 - x0 + g * x1, b = x3 - x0 + hh * x3, c = x0;
    var d = y1 - y0 + g * y1, e = y3 - y0 + hh * y3, f = y0;
    return 'matrix3d(' +
      (a / w) + ',' + (d / w) + ',0,' + (g / w) + ',' +
      (b / h) + ',' + (e / h) + ',0,' + (hh / h) + ',' +
      '0,0,1,0,' + c + ',' + f + ',0,1)';
  }

  // Photo point -> stage px. Inverse of the hero shader's screen->photo
  // sampling (fitUv cover/contain, depth-modulated offset, swipe pan, dolly).
  function project(u, v, d, st, out) {
    var qx = u - 0.5;
    var qy = (1 - v) - 0.5; // shader uv space is bottom-up
    var sxf, syf;
    if (st.fit < 0.5) { // cover (crop)
      if (st.viewA > st.imgA) { sxf = 1; syf = st.imgA / st.viewA; }
      else { sxf = st.viewA / st.imgA; syf = 1; }
    } else {            // contain (letterbox)
      if (st.viewA > st.imgA) { sxf = st.viewA / st.imgA; syf = 1; }
      else { sxf = 1; syf = st.imgA / st.viewA; }
    }
    qx /= sxf;
    qy /= syf;
    var ux = qx + 0.5 - st.ox * d * st.ds - st.px;
    var uy = qy + 0.5 - st.oy * d * st.ds - st.py;
    var vx = (ux - st.fx) * st.z + st.fx;
    var vy = (uy - st.fy) * st.z + st.fy;
    out[0] = vx * st.sw;
    out[1] = (1 - vy) * st.sh;
  }

  function staticState(inst) {
    var contain = (inst.root.getAttribute('data-mobile-fit') || '') === 'contain' &&
                  window.innerWidth <= 640;
    return {
      z: 1, fx: 0.5, fy: 0.5,
      px: 0, py: 0, ox: 0, oy: 0, ds: 0,
      imgA: inst.imgA,
      viewA: inst.sw / (inst.sh || 1),
      fit: contain ? 1 : 0,
      sw: inst.sw, sh: inst.sh,
      mix: 0
    };
  }

  function apply(inst, st) {
    if (!st.sw || !st.sh) return;
    var mix = st.mix || 0;
    var destMenu = !!(st.dest && st.dest.indexOf(MENU_KEY) !== -1);

    // Which wall are the boards glued to right now? Up to the crossfade
    // midpoint: the hero photo's wall. Past it (flying into the bundled menu
    // scene): the destination photo's wall, through scene-2 camera params
    // (own zoom/focus/aspect, no swipe pan — mirrors the shader).
    var toMenu = destMenu && mix >= 0.5;
    var quads = toMenu ? MENU_SCREENS : HERO_SCREENS;
    var p = toMenu ? {
      z: st.z2, fx: st.fx2, fy: st.fy2,
      px: 0, py: 0, ox: st.ox, oy: st.oy, ds: st.ds,
      imgA: st.imgA2, viewA: st.viewA, fit: st.fit,
      sw: st.sw, sh: st.sh
    } : st;

    var pt = [0, 0];

    // In contain (letterboxed) mode, clip the layer to the photo rect so the
    // partially-visible panel 2 can't spill past the photo onto the bars.
    if (p.fit > 0.5) {
      project(0, 0, 0, p, pt);
      var lx = pt[0], ty = pt[1];
      project(1, 1, 0, p, pt);
      var clip = 'inset(' + Math.max(0, ty) + 'px ' + Math.max(0, p.sw - pt[0]) + 'px ' +
                 Math.max(0, p.sh - pt[1]) + 'px ' + Math.max(0, lx) + 'px)';
      if (clip !== inst.lastClip) { inst.layer.style.clipPath = clip; inst.lastClip = clip; }
    } else if (inst.lastClip) {
      inst.layer.style.clipPath = '';
      inst.lastClip = '';
    }

    for (var i = 0; i < inst.items.length; i++) {
      var item = inst.items[i];
      var quad = quads[item.index];
      var pts = [];
      var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (var k = 0; k < 4; k++) {
        project(quad[k][0], quad[k][1], quad[k][2], p, pt);
        pts.push([pt[0], pt[1]]);
        if (pt[0] < minX) minX = pt[0];
        if (pt[0] > maxX) maxX = pt[0];
        if (pt[1] < minY) minY = pt[1];
        if (pt[1] > maxY) maxY = pt[1];
      }
      // Skip compositing for panels projected outside the stage (in the hero
      // photo, panels 3-5 lie past the right edge of the frame).
      if (maxX < -40 || minX > p.sw + 40 || maxY < -40 || minY > p.sh + 40) {
        if (!item.offstage) { item.el.style.visibility = 'hidden'; item.offstage = true; }
        continue;
      }
      if (item.offstage) { item.el.style.visibility = ''; item.offstage = false; }
      var m = quadMatrix(item.w, item.h, pts);
      if (m) item.el.style.transform = m;
    }

    // Crossfade: boards dissolve out with the hero, and — when the camera is
    // flying into the bundled menu-wall photo — dissolve back in glued to the
    // wall there (the quad set above switches at the hidden midpoint).
    var fade;
    if (mix <= 0.02) fade = 1;
    else if (!destMenu) fade = Math.max(0, 1 - mix * 2.5);
    else if (mix < 0.5) fade = Math.max(0, 1 - mix * 2.5);
    else fade = Math.min(1, (mix - 0.5) * 2.5);
    if (fade !== inst.lastFade) {
      inst.layer.style.opacity = fade === 1 ? '' : String(fade);
      inst.lastFade = fade;
    }
  }

  function measure(inst) {
    inst.sw = inst.stage.clientWidth;
    inst.sh = inst.stage.clientHeight;
  }

  function loadFrames(inst) {
    if (inst.framesLoaded) return;
    inst.framesLoaded = true;
    inst.items.forEach(function (item) {
      var frame = item.el.querySelector('iframe[data-src]');
      if (!frame) return;
      var src = frame.getAttribute('data-src');
      function goLive() { item.el.classList.add('is-live'); }
      frame.addEventListener('load', function () {
        if (frame.getAttribute('src')) goLive();
      });
      frame.src = src;
      // Some browsers/webviews swallow iframe load events. If the board app
      // answers on the network, reveal anyway after it had time to paint —
      // and if it is unreachable, the panels stay as photographed.
      setTimeout(function () {
        if (item.el.classList.contains('is-live')) return;
        fetch(src, { mode: 'no-cors', cache: 'no-store' }).then(function () {
          setTimeout(goLive, 800);
        }).catch(function () { /* app down — leave the TV dark */ });
      }, 3500);
    });
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    var layer = root.querySelector('[data-screens]');
    var stage = root.querySelector('.bohdy__stage');
    if (!layer || !stage) return;

    // Quads are authored for the bundled interior photo only.
    var heroSrc = root.getAttribute('data-hero-src') || '';
    if (heroSrc.indexOf(HERO_KEY) === -1) return;

    var items = [];
    layer.querySelectorAll('.bohdy__screen').forEach(function (el, i) {
      if (i >= HERO_SCREENS.length) return;
      items.push({
        el: el,
        index: i,
        w: el.offsetWidth || 360,
        h: el.offsetHeight || 640,
        offstage: false
      });
    });
    if (!items.length) return;

    var inst = {
      root: root, layer: layer, stage: stage, items: items,
      imgA: FALLBACK_ASPECT, sw: 0, sh: 0,
      dynamic: false, framesLoaded: false, lastFade: 1, lastClip: ''
    };
    instances.set(root, inst);

    var img = root.querySelector('.bohdy__photo img');
    function readAspect() {
      if (img && img.naturalWidth && img.naturalHeight) {
        inst.imgA = img.naturalWidth / img.naturalHeight;
      }
    }
    readAspect();
    if (img && !img.complete) img.addEventListener('load', function () {
      readAspect();
      if (!inst.dynamic) apply(inst, staticState(inst));
    }, { once: true });

    function restatic() {
      measure(inst);
      if (!inst.dynamic) apply(inst, staticState(inst));
    }
    inst.ro = new ResizeObserver(restatic);
    inst.ro.observe(stage);
    restatic();

    // Per-frame hook driven by bohdy-hero3d.js; null = 3D torn down.
    root._bohdyScreens = function (st) {
      if (!st) {
        inst.dynamic = false;
        restatic();
        return;
      }
      inst.dynamic = true;
      st.sw = inst.sw;
      st.sh = inst.sh;
      apply(inst, st);
    };

    // Defer the five board iframes until the page (hero included) has loaded,
    // so they never compete with the LCP image.
    function armFrames() {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(function () { loadFrames(inst); }, { timeout: 2500 });
      } else {
        setTimeout(function () { loadFrames(inst); }, 1200);
      }
    }
    if (document.readyState === 'complete') armFrames();
    else window.addEventListener('load', armFrames, { once: true });
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    if (inst.ro) inst.ro.disconnect();
    delete root._bohdyScreens;
    instances.delete(root);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e || !e.target) return;
    var root = e.target.querySelector(SECTION_SELECTOR);
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e || !e.target) return;
    var root = e.target.querySelector(SECTION_SELECTOR);
    if (root) destroy(root);
  });
})();
